/**
 * ============================================================================
 * ProgramDetailsOpenRfi — Service (API calls)
 * ============================================================================
 * Pure fetch wrappers. No JSX, no React state. Reused by all three
 * ProgramDetailsOpenRfi variants (organisms/NewBannerForm, sections/NewBannerFormLp,
 * organisms/modal/ConnectModalMba) so the request contracts stay identical.
 * ============================================================================
 */
import { getIpJSONData } from "@/context/ip.storage.utils";
import { leadCreateFetch } from "@/utils/connectCounsellor";
import { withLeadScholarship } from "@/helpers/leadScholarship";

export interface LeadCheckPayload {
  phoneNumber: string;
  emailId: string;
  fullName: string;
  countryCode: string;
  uniqueId: string;
  commConsentDate: string;
}

/** POST /api/leadsquared.com/lead-check — silent, best-effort dedupe ping. */
export async function checkExistingLead(payload: LeadCheckPayload): Promise<void> {
  try {
    const response = await fetch("/api/leadsquared.com/lead-check", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        PhoneNo: payload.phoneNumber,
        EmailID: payload.emailId,
        Name: payload.fullName,
        CountryCode: payload.countryCode,
        PageUrl: window.location.href,
        CommConsent: payload.commConsentDate,
        UniqueId: payload.uniqueId,
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to submit phone number");
    }
  } catch (error) {
    console.error("Error submitting phone number:", error);
  }
}

/** Ensures/returns the anonymous uniqueLeadId used for lead-check dedupe. */
export function getOrCreateUniqueLeadId(): string {
  let uniqueId = localStorage.getItem("uniqueLeadId");
  if (!uniqueId) {
    uniqueId = `uid-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
    localStorage.setItem("uniqueLeadId", uniqueId);
  }
  return uniqueId;
}

export interface CreateLeadOptions {
  form: Record<string, any>;
  meta: Record<string, any>;
  endpoint: "lead-create-new" | "lead-create-lp-new" | "jiopc-lead-create-new";
  /** Extra fields merged into `form` (e.g. Custom5/Custom10), matching the
   * original leadSquareApiCall(payload)/leadSquareApiCallLpPage(payload)
   * merge-into-form behavior. */
  formExtra?: Record<string, any>;
  /**
   * Root-level body fields (ConnectModal brochure case puts
   * `mx_Enquery_Source` beside `form`/`meta`, not inside `form`).
   */
  bodyExtra?: Record<string, any>;
}

/**
 * POST /api/salesforce.com/lead-create-new or /lead-create-lp-new.
 * `endpoint` preserves the existing isLambdaAPI branch used by every variant.
 * Note: the pre-refactor non-lambda call omitted a Content-Type header while
 * the lambda/LP call included it; both now send it explicitly since the
 * body is JSON-encoded either way and the API routes accept it.
 */
export async function createLead({ form, meta, endpoint, formExtra, bodyExtra }: CreateLeadOptions) {
  const response = await leadCreateFetch(`/api/salesforce.com/${endpoint}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      ipstack: getIpJSONData(),
      form: withLeadScholarship({ ...form, ...formExtra }),
      meta,
      ...bodyExtra,
    }),
  });

  if (typeof response === "string") {
    return JSON.parse(response);
  }
  return response.json();
}

/** POST /api/prospect-activity — fire-and-forget, matches existing behavior. */
export function createProspectActivity(leadResponse: any) {
  fetch("/api/prospect-activity", {
    method: "post",
    body: JSON.stringify({ leadResponse }),
  }).then(() => {
    // do nothing — matches existing fire-and-forget behavior
  });
}

/** POST /api/connect-counsellor. */
export { connectCounsellor } from "@/utils/connectCounsellor";
